/*
  LadyCloshop — barra de avisos fija encima de la cabecera.
  Al bajar (fuera de lo más alto) se pone más fina; arriba del todo vuelve a su tamaño.
  Deja en <html>:
  - .lc-scrolled mientras no se está arriba del todo;
  - --lc-header-stack: alto de barra + cabecera (para la columna fija de la ficha y los saltos).
*/
(() => {
  const root = document.documentElement;
  const bar = document.querySelector('.lc-announcement')?.closest('.shopify-section');
  const header = document.querySelector('.section-header');
  const ENTER = 24; // px bajados para pasar a fina
  const LEAVE = 4; // px desde arriba para volver a normal

  const measure = () => {
    const barH = bar ? bar.getBoundingClientRect().height : 0;
    const headerH = header ? header.getBoundingClientRect().height : 0;
    root.style.setProperty('--lc-header-stack', `${Math.round(barH + headerH)}px`);
  };

  let ticking = false;
  const onScroll = () => {
    ticking = false;
    const y = window.scrollY;
    if (y > ENTER) root.classList.add('lc-scrolled');
    else if (y <= LEAVE) root.classList.remove('lc-scrolled');
  };
  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(onScroll);
    },
    { passive: true }
  );

  const observer = new ResizeObserver(measure);
  if (bar) observer.observe(bar);
  if (header) observer.observe(header);
  onScroll();
  measure();
})();

/*
  LadyCloshop — cabecera de escritorio: si las colecciones del menú no caben en una fila
  (pantallas medianas o nombres largos), la cabecera pasa a la versión compacta de la ficha:
  logo, buscador, cesta y el menú en el botón lateral. Así nunca se corta ningún nombre.
*/
(() => {
  const header = document.querySelector('.section-header .header--lc');
  const list = header?.querySelector('.header__inline-menu .list-menu--inline');
  if (!header || !list || header.classList.contains('header--compact')) return;

  const desktop = window.matchMedia('(min-width: 990px)');
  const COMPACT = ['lc-menu-overflow', 'header--compact', 'drawer-menu'];

  const check = () => {
    header.classList.remove(...COMPACT);
    if (!desktop.matches) return;
    // La fila puede salir de la caja de la cabecera (1300 px), pero no del ancho de la pantalla
    // menos los márgenes laterales.
    const styles = getComputedStyle(header);
    const room = document.documentElement.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);
    if (list.scrollWidth > room) header.classList.add(...COMPACT);
    header.classList.add('lc-menu-checked');
  };

  let frame = 0;
  const schedule = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(check);
  };
  desktop.addEventListener('change', schedule);
  window.addEventListener('resize', schedule);
  document.fonts?.ready.then(schedule);
  check();
})();

/*
  LadyCloshop — cabecera de escritorio: la fila del menú (colecciones) se recoge al bajar
  y vuelve en cuanto se sube un poco. La fila del logo, buscador y cesta no se mueve nunca.

  Para que no sea molesto:
  - No se recoge hasta haber bajado más allá de la cabecera, ni por movimientos pequeños
    (hace falta recorrer unos píxeles seguidos en la misma dirección).
  - Al subir, reaparece casi de inmediato; arriba del todo siempre está visible.
  - No se recoge con un menú desplegado, con el ratón encima o con el foco del teclado dentro;
    si el foco entra en el menú recogido, aparece.
  - Se recorta con clip-path (no cambia la altura de la cabecera): la página no da saltos.
  - Con «reducir movimiento» cambia sin animación.
*/
(() => {
  const section = document.querySelector('.section-header');
  const wrapper = section?.querySelector('.header-wrapper');
  const header = section?.querySelector('.header');
  const nav = section?.querySelector('.header__inline-menu');
  if (!wrapper || !header || !nav) return;

  const desktop = window.matchMedia('(min-width: 990px)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const HIDE_AFTER = 16; // px seguidos bajando
  const SHOW_AFTER = 8; // px seguidos subiendo
  const TOP_ZONE = 320; // px desde arriba en los que el menú siempre se ve

  let hidden = false;
  let cut = 0;
  let lastY = window.scrollY;
  let travel = 0;
  let ticking = false;

  // Altura de la fila del menú (desde donde empieza la 2.ª fila de la rejilla hasta el borde)
  const measure = () => {
    cut = 0;
    if (desktop.matches && nav.offsetParent !== null) {
      const styles = getComputedStyle(header);
      const rows = styles.gridTemplateRows.split(' ').map(parseFloat);
      if (rows.length > 1) {
        const secondRowTop = header.getBoundingClientRect().top + parseFloat(styles.paddingTop) + rows[0];
        cut = Math.max(0, Math.round(wrapper.getBoundingClientRect().bottom - secondRowTop));
      }
    }
    section.style.setProperty('--lc-menu-cut', `${cut}px`);
    if (!cut) show();
  };

  const busy = () =>
    section.querySelector('details[open]') ||
    nav.matches(':hover, :focus-within') ||
    [...section.querySelectorAll('predictive-search')].some((search) => search.isOpen);

  function hide() {
    if (hidden || !cut || busy()) return;
    hidden = true;
    section.classList.remove('lc-menu-revealing');
    section.classList.add('lc-menu-hidden');
  }

  function show() {
    if (!hidden) return;
    hidden = false;
    section.classList.remove('lc-menu-hidden');
    if (!reduceMotion.matches) section.classList.add('lc-menu-revealing');
  }

  const onScroll = () => {
    ticking = false;
    const y = Math.max(0, window.scrollY);
    const delta = y - lastY;
    lastY = y;
    if (!delta) return;

    if (!cut || y < TOP_ZONE) {
      travel = 0;
      show();
      return;
    }

    travel = Math.sign(delta) === Math.sign(travel) ? travel + delta : delta;
    if (travel > HIDE_AFTER) hide();
    else if (travel < -SHOW_AFTER) show();
  };

  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(onScroll);
    },
    { passive: true }
  );

  wrapper.addEventListener('animationend', (event) => {
    if (event.animationName === 'lc-menu-show') section.classList.remove('lc-menu-revealing');
  });
  nav.addEventListener('focusin', show);
  desktop.addEventListener('change', measure);
  new ResizeObserver(measure).observe(wrapper);
  measure();
})();

// Desplegables del menú (Ropa, Accesorios): en ordenador con ratón se abren al pasar por encima,
// además de con clic o teclado. Se cierran con un pequeño margen para poder bajar al panel, y
// siempre con un fundido (.lc-closing) en vez de desaparecer de golpe.
(() => {
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const menus = document.querySelectorAll('.header--lc .header__inline-menu header-menu details');
  if (!menus.length) return;

  const open = (details) => {
    details.classList.remove('lc-closing');
    details.open = true;
    details.querySelector('summary').setAttribute('aria-expanded', true);
  };

  const close = (details) => {
    if (!details.open || details.classList.contains('lc-closing')) return;
    const finish = () => {
      if (!details.classList.contains('lc-closing')) return; // se volvió a abrir a tiempo
      details.classList.remove('lc-closing');
      details.open = false;
      details.querySelector('summary').setAttribute('aria-expanded', false);
    };
    if (reduced.matches) {
      details.classList.add('lc-closing');
      finish();
      return;
    }
    details.classList.add('lc-closing');
    setTimeout(finish, 230);
  };

  menus.forEach((details) => {
    const item = details.closest('li');
    const summary = details.querySelector('summary');
    const wrapper = details.closest('header-menu');
    let timer = 0;

    // Al salir con el teclado o pulsar fuera, el tema también cierra con fundido
    if (wrapper) wrapper.close = () => close(details);

    item.addEventListener('mouseenter', () => {
      if (!canHover.matches) return;
      clearTimeout(timer);
      menus.forEach((other) => {
        if (other !== details) close(other);
      });
      open(details);
    });

    item.addEventListener('mouseleave', () => {
      if (!canHover.matches) return;
      timer = setTimeout(() => close(details), 180);
    });

    summary.addEventListener('click', (event) => {
      if (!details.open || details.classList.contains('lc-closing')) {
        if (details.classList.contains('lc-closing')) {
          event.preventDefault();
          open(details);
        }
        return;
      }
      event.preventDefault();
      // Abierto por el ratón y aún encima: el clic no lo cierra de golpe
      if (canHover.matches && item.matches(':hover')) return;
      close(details);
    });
  });
})();
